import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSubscription } from '../context/SubscriptionContext'
import { useState, useEffect, useRef, useMemo } from 'react'
import { subscriptionService, deliveryService, authService } from '../services'
import { PlanDetails, DeliverySchedule, Subscription, PlanItem } from '../types/api'
import {
  HeroSection,
  FeaturesSection,
  FruitsSection,
  HowItWorksSection,
  OurServicesSection,
  SubscriptionPlansSection,
  SendAsGiftSection,
  CustomerReviewsSection,
  Footer
} from '../components/home'
import ServiceSwitcher from '../components/ServiceSwitcher'
import InstagramEmbed from '../components/common/InstagramEmbed'
import apiClient from '../services/api'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import ShutdownNotice from '../components/MaintenanceNotice'

// localStorage key for promotion code
const PROMO_CODE_STORAGE_KEY = 'piika_promo_code';

export default function CatGrass() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscriptions, isLoading: isSubscriptionLoading } = useSubscription();
  
  // State management
  const [planDetails, setPlanDetails] = useState<PlanDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'five-star' | 'premium' | 'standard'>('all');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [promoInfo, setPromoInfo] = useState<string | null>(null);
  
  // Auto-show signup modal on first visit
  const [hasShownModal, setHasShownModal] = useState(false);
  const isInitialMount = useRef(true);

  // Check for promotion code in URL parameters
  useEffect(() => {
    const promoCodeFromUrl = searchParams.get('code');
    if (promoCodeFromUrl) {
      localStorage.setItem(PROMO_CODE_STORAGE_KEY, promoCodeFromUrl);
      setPromoInfo(t('promoCodeApplied', { code: promoCodeFromUrl }));
      
      // Remove the code from URL without refreshing
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('code');
      const newUrl = window.location.pathname + (newSearchParams.toString() ? '?' + newSearchParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);

  // Auto-show modal on first visit after a delay
  useEffect(() => {
    const hasVisited = localStorage.getItem('piika_visited');
    if (!hasVisited && !hasShownModal && !isSubscriptionLoading) {
      const timer = setTimeout(() => {
        // Check if user is authenticated
        const token = localStorage.getItem('piika_access_token');
        if (!token) {
          setIsModalVisible(true);
          setHasShownModal(true);
          localStorage.setItem('piika_visited', 'true');
        }
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [hasShownModal, isSubscriptionLoading]);

  // Fetch subscription plans and other data
  useEffect(() => {
    const fetchData = async () => {
      if (isInitialMount.current) {
        setIsLoading(true);
      }
      
      try {
        // Fetch cat grass subscription plans
        const plansData = await subscriptionService.getPlans();
        console.log('All plans data:', plansData);
        
        // Filter for cat grass plans
        const filteredPlans = plansData.filter((plan: PlanItem) => 
          plan.service_type === 'cat_grass'
        );
        console.log('Filtered cat grass plans:', filteredPlans);
        
                 if (filteredPlans.length > 0) {
           // Format the data structure
           const standardPlan = filteredPlans.find((plan: any) => plan.type === 'standard');
           const premiumPlan = filteredPlans.find((plan: any) => plan.type === 'premium');
           
           const formattedPlans: PlanDetails = {
             standard: standardPlan ? {
               price: standardPlan.price,
               price_usd: standardPlan.price_usd,
               trial_price: standardPlan.trial_price,
               trial_price_usd: standardPlan.trial_price_usd,
               original_price: standardPlan.original_price,
               description: standardPlan.description,
               features: standardPlan.features || []
             } : {
               price: 249,
               price_usd: 7.99,
               trial_price: 99,
               trial_price_usd: 2.99,
               features: [t('plansSection.features.weeklyFreshCatGrass'), t('features.naturallyGrown'), t('features.petSafe')]
             },
             premium: premiumPlan ? {
               price: premiumPlan.price,
               price_usd: premiumPlan.price_usd,
               trial_price: premiumPlan.trial_price,
               trial_price_usd: premiumPlan.trial_price_usd,
               original_price: premiumPlan.original_price,
               description: premiumPlan.description,
               features: premiumPlan.features || []
             } : {
               price: 349,
               price_usd: 11.99,
               trial_price: 149,
               trial_price_usd: 4.99,
               features: [t('plansSection.features.weeklyFreshCatGrass'), t('features.multipleVarieties'), t('features.naturallyGrown'), t('features.ceramicPlanters')]
             }
           };
           setPlanDetails(formattedPlans);
        } else {
          // If no cat grass plans found, create fallback plans
          const fallbackPlans: PlanDetails = {
            standard: {
              price: 249,
              price_usd: 7.99,
              trial_price: 99,
              trial_price_usd: 2.99,
              features: [t('plansSection.features.weeklyFreshCatGrass'), t('features.naturallyGrown'), t('features.petSafe')]
            },
            premium: {
              price: 349,
              price_usd: 11.99,
              trial_price: 149,
              trial_price_usd: 4.99,
              features: [t('plansSection.features.weeklyFreshCatGrass'), t('features.multipleVarieties'), t('features.naturallyGrown'), t('features.ceramicPlanters')]
            }
          };
          setPlanDetails(fallbackPlans);
        }

        // Fetch cat grass reviews (placeholder - you may need to implement this)
        try {
          const reviewsResponse = await apiClient.get('/reviews', {
            params: { service_type: 'cat_grass', limit: 20 }
          });
          setReviews(reviewsResponse.data?.data || []);
        } catch (reviewError) {
          console.log('No cat grass reviews available yet');
          setReviews([]);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching cat grass plans:', err);
        setError(t('errors.loadSubscriptionData'));
      } finally {
        setIsLoading(false);
        isInitialMount.current = false;
      }
    };
    
    // Only fetch data if subscriptions finished loading, to avoid race conditions
    if (!isSubscriptionLoading) {
      fetchData();
    }
    
  }, [isSubscriptionLoading, t]);

  // Handle selecting a subscription plan
  const handlePlanSelect = (plan: 'standard' | 'premium') => {
    // Get the saved promotion code from localStorage
    const savedPromoCode = localStorage.getItem(PROMO_CODE_STORAGE_KEY);
    
    // Navigate to subscription page with cat grass service type
    navigate('/subscription', { 
      state: { 
        selectedPlan: plan,
        fromPage: 'cat_grass',
        serviceType: 'cat_grass',  // Set service type to cat_grass
        giftType: null,  // Explicitly not a gift
        isGift: false,   // Extra property to ensure it's not treated as a gift
        clearGiftMode: true,  // Explicitly clear any existing gift mode
        // Include the promotion code in the navigation state if available
        promotionCode: savedPromoCode || undefined
      } 
    });
  };

  // Determine the combined loading state
  const isPageLoading = isSubscriptionLoading || isLoading;

  // Add a function to filter reviews
  const filteredReviews = useMemo(() => {
    switch(reviewFilter) {
      case 'five-star':
        return reviews.filter(review => review.rating === 5);
      case 'premium':
        return reviews.filter(review => review.planType === 'premium');
      case 'standard':
        return reviews.filter(review => review.planType === 'standard');
      default:
        return reviews;
    }
  }, [reviews, reviewFilter]);

  // Function to mark review as helpful
  const markReviewAsHelpful = (reviewId: string) => {
    setReviews(reviews.map(review => 
      review.id === reviewId
        ? { ...review, helpfulCount: review.helpfulCount + 1 }
        : review
    ));
  };

  // Show error state
  if (error && !isPageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('errors.loadSubscriptionData')}
          </h2>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Service Switcher */}
      <ServiceSwitcher />
      
      {/* Shutdown Notice */}
      <ShutdownNotice />
      
      {/* Hero Section */}
      <HeroSection
        isLoading={isPageLoading}
        subscriptions={subscriptions}
        handlePlanSelect={handlePlanSelect}
        promoInfo={promoInfo}
        serviceType="cat_grass"
      />

      {/* Subscription Plans Section - moved up for better conversion */}
      <div className="px-0">
        <SubscriptionPlansSection
          isLoading={isPageLoading}
          planDetails={planDetails ? [
            { 
              ...planDetails.standard, 
              type: 'standard' as const, 
              id: 'standard',
              service_type: 'cat_grass' as const
            },
            { 
              ...planDetails.premium, 
              type: 'premium' as const, 
              id: 'premium',
              service_type: 'cat_grass' as const
            }
          ].filter(Boolean) : null}
          handlePlanSelect={handlePlanSelect}
          promoInfo={promoInfo}
          serviceType="cat_grass"
        />
      </div>

      {/* Donation Information Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {t('catGrass.donation.title')}
            </h2>
            <div className="w-20 h-1 bg-green-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {t('catGrass.donation.description')}
            </p>
            <div className="bg-white rounded-lg shadow-lg p-6 inline-block">
              <div className="flex items-center justify-center mb-4">
                <span className="text-4xl font-bold text-green-600">100%</span>
                <span className="ml-3 text-gray-600 text-lg">{t('catGrass.donation.percentage')}</span>
              </div>
              <p className="text-gray-600 text-sm">
                {t('catGrass.donation.impact')}
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🐱</span>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{t('catGrass.donation.benefit1.title')}</h3>
              <p className="text-gray-600 text-sm">{t('catGrass.donation.benefit1.description')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">❤️</span>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{t('catGrass.donation.benefit2.title')}</h3>
              <p className="text-gray-600 text-sm">{t('catGrass.donation.benefit2.description')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🏠</span>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">{t('catGrass.donation.benefit3.title')}</h3>
              <p className="text-gray-600 text-sm">{t('catGrass.donation.benefit3.description')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Send as Gift Section */}
      <SendAsGiftSection />

      {/* Features Section */}
      <FeaturesSection />

      {/* Instagram Section */}
      <div className="bg-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('common.instagram.followUs')}</h3>
            <p className="text-gray-600 text-md leading-relaxed">
              {t('common.instagram.gratitudeMessage')}<br/>
              {t('common.instagram.followDescription')}<br/>
              {t('common.instagram.handle')}
            </p>
          </div>
          
          <div className="overflow-x-auto overflow-y-hidden">
            <div className="flex gap-4 px-4 w-max">
              <div className="flex-shrink-0 w-80">
                <InstagramEmbed
                  postUrl="https://www.instagram.com/p/DJRNzuhNmJq/"
                  captioned={true}
                  maxWidth={320}
                  className="w-full"
                />
              </div>
              <div className="flex-shrink-0 w-80">
                <InstagramEmbed
                  postUrl="https://www.instagram.com/p/DI7_MjCvhNh/"
                  captioned={true}
                  maxWidth={320}
                  className="w-full"
                />
              </div>
              <div className="flex-shrink-0 w-4"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Services Section */}
      <OurServicesSection serviceType="cat_grass" />

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Customer Reviews Section */}
      {reviews.length > 0 && (
        <CustomerReviewsSection />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
} 